import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef } from 'react'

import ContentfulPreOrderModal from '@src/quince-site-common/src/domains/contentful/ContentfulPreOrder'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOption } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, { getIsSelectedColorAppExclusive } from '@src/quince-site-common/src/helpers/productHelper'
import { getOverriddenOfferMessage } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { getFinalSaleEventProperties } from '@src/quince-site-common/src/utils/finalSaleEventUtility'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import { trackImpressions } from '@src/quince-site-pdp/src/organisms/productPageSections/Stylitics/utils'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import Link from '../link'
import { CTA_TYPE } from '../productCTA/productCTAConstants'
import ProductMessage from '../productCTA/productMessage'
import { ProductCustomBannerMessage } from '../productCustomBannerMessage'
import { documentToReactComponentsTextOptions, VariantPurchaseInfo } from '../productForm'
import styles from '../productForm/productForm.module.scss'
import { default as ProductOfferMessage } from '../productOfferMessage'

import { getAppExclusiveMessageEventProperties } from './utils'

// @i18n-ignore
const QUINCE_APP = 'Quince App'

export interface GreyBoxesProps {
  product?: Product
  productOptions?: ProductOption[]
  selectedOptions?: ProductVariantOption[]
  selectedVariantPurchaseInfo?: VariantPurchaseInfo
  productCTAType: CTA_TYPE
  isReturnable?: boolean
  subDescriptionText?: string
  hideOfferMessage?: boolean
  offerMessageFromPC?: string
  hideMtoText?: boolean
  preOrderModalData?: ContentfulPreOrderModal
  isFinalSaleProduct?: boolean
  isQuickATCModal?: boolean
  handleShopInApp: ({
    product,
    selectedOptions,
  }: {
    product?: Product
    selectedOptions?: ProductVariantOption[]
  }) => void
}

const GreyBoxes = (props: GreyBoxesProps) => {
  const { t } = useTranslation('common')
  const { productCTAType } = props
  const appExclusiveImpressionRef = useRef<HTMLDivElement>(null)
  const isColorAppExclusive = getIsSelectedColorAppExclusive(props.productOptions, props.selectedOptions)

  const offerMessage = getOverriddenOfferMessage(props.offerMessageFromPC, props.product, props.selectedOptions)

  const shouldShowMTOText =
    !props.hideMtoText &&
    props.selectedVariantPurchaseInfo?.showMadeToOrder &&
    props.selectedVariantPurchaseInfo.madeToOrderText

  const shippingInfoText = props.product && productHelper.getShippingInfoText(props.product)

  const showCustomBannerMessage = !productHelper.getIsAgeRestrictedProduct(props.product as Product)

  const { promo_price } = getFinalSaleEventProperties(props.product ?? ({} as Product), props.selectedOptions ?? [])
  const propertyOverrides = {
    discounted_price: promo_price ?? props.product?.variants?.[0]?.price,
    price: props.product?.variants?.[0]?.price,
  }

  const onAppExclusiveImpression = () => {
    trackEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, {
      ...getAppExclusiveMessageEventProperties({
        product: props.product ?? ({} as Product),
        selectedOptions: props.selectedOptions ?? [],
        isFinalSaleProduct: props.isFinalSaleProduct ?? false,
        isColorAppExclusive,
        propertyOverrides,
        action: 'app_exclusive_message',
      }),
    })
  }
  useEffect(() => {
    if (appExclusiveImpressionRef.current && isColorAppExclusive) {
      trackImpressions(appExclusiveImpressionRef.current, onAppExclusiveImpression, {}, {})
    }
  }, [isColorAppExclusive])

  if (isColorAppExclusive) {
    return (
      <div
        className={classnames(
          styles.container__subDescText,
          styles.container__subDescText_adjustedMargin,
          styles.container__subDescText_newDesignV2,
        )}
        ref={appExclusiveImpressionRef}
      >
        {t('app_exclusive_available')}
        {` `}
        <Link
          onClick={() => {
            props.handleShopInApp({ product: props.product, selectedOptions: props.selectedOptions })
          }}
          className={classnames(styles.container__subDescText__und_offset, 'text-decor--underline d-inline')}
        >
          {QUINCE_APP}
        </Link>
        {`. `}
        {t('app_exclusive_early_access')}
      </div>
    )
  }

  return (
    <>
      {(shouldShowMTOText ||
        (!props.isReturnable && !productHelper.getIsAgeRestrictedProduct(props.product as Product))) && (
        <div
          className={classnames(styles.container__mtoText, styles.container__mtoText_newDesignV2, {
            [styles.container__mtoText_newDesignV2_hidden ?? '']: !shouldShowMTOText && !shippingInfoText,
          })}
        >
          {shouldShowMTOText ? (
            <>
              {props.selectedVariantPurchaseInfo?.madeToOrderText
                ? documentToReactComponents(
                    props.selectedVariantPurchaseInfo.madeToOrderText,
                    documentToReactComponentsTextOptions,
                  )
                : null}
            </>
          ) : (
            shippingInfoText && (
              //eslint-disable-next-line
              <div dangerouslySetInnerHTML={{ __html: getSanitizedHtml(shippingInfoText as string) }} />
            )
          )}
        </div>
      )}
      {props.subDescriptionText && (
        <div
          className={classnames(
            styles.container__subDescText,
            styles.container__subDescText_adjustedPadding,
            styles.container__subDescText_newDesignV2,
          )}
          data-testid="sub-description-text"
        >
          {
            <div
              className={classnames(styles.container__subDescText__subText, {
                [`${styles.container__subDescText__textColorBlack}`]: props.isReturnable,
              })}
              //eslint-disable-next-line
              dangerouslySetInnerHTML={{ __html: getSanitizedHtml(props.subDescriptionText) }}
            />
          }
        </div>
      )}

      <ProductOfferMessage
        hideOfferMessage={props.hideOfferMessage}
        offerMessageFromPC={offerMessage}
        productId={props.product?.spid as number}
        showFinalSaleMessage={props.isFinalSaleProduct}
      />
      <ProductMessage
        type={productCTAType}
        selectedVariantPurchaseInfo={props.selectedVariantPurchaseInfo}
        preOrderModalData={props.preOrderModalData}
        isQuickATCModal={props.isQuickATCModal}
      />
      {showCustomBannerMessage && (
        <ProductCustomBannerMessage
          customBannerMessageFromPC={productHelper.getPDPCustomBannerText(props.product)}
          productId={props.product?.spid as number}
        />
      )}
    </>
  )
}

export default GreyBoxes
